import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../common/Button";

import MeterReport from "./MeterReport";
import CropReport from "./CropReport";
import SoilReport from "./SoilReport";
import ReportConfirmation from "./ReportConfirmation";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faCircleExclamation,
  faCircleCheck,
  faClock,
  faTriangleExclamation,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import useReportValidation from "./useReportValidation";
import ConfirmModal from "../common/ConfirmModal";
import useFetch from "../../hooks/useFetch";

library.add(faCircleExclamation, faCircleCheck, faClock, faTriangleExclamation, faCircleInfo);

const COMMON_TAB_STYLES = "flex gap-4 justify-center items-center flex-1 h-10 [&:not(:last-child)]:border-r border-slate-400 text-center text-lg border-b";

function ReportTab({ label, active, onClick, status, disabled }) {
  let className;
  if (active) {
    className = `${COMMON_TAB_STYLES} font-bold border-b-transparent`;
  } else {
    className = `${COMMON_TAB_STYLES} disabled:bg-stone-300 disabled:text-stone-500`;
  }

  let icon;
  switch (typeof status === 'string' ? status : status.displayLevel || status.level) {
    case "error":
      icon = (
        <FontAwesomeIcon
          icon="circle-exclamation"
          title="Some Values are Invalid"
          className="text-red-600"
          size="lg"
        />
      );
      break;
    case "complete":
      icon = (
        <FontAwesomeIcon
          icon="circle-check"
          title="Section Complete"
          className="text-green-600"
          size="lg"
        />
      );
      break;
    case "incomplete":
      icon = (
        <FontAwesomeIcon
          icon="clock"
          title="Section Incomplete"
          className="text-yellow-600"
          size="lg"
        />
      );
      break;
    case "warning":
      icon = (
        <FontAwesomeIcon
          icon="triangle-exclamation"
          title="Section May Have Errors"
          className="text-orange-400"
          size="lg"
        />
      );
      break;
    default:
      break;
  }

  return (
    <button
      className={className}
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
      {label}
    </button>
  );
}

export default function ReportFormsHolder({
  reportId,
  requireTractConfirm,
  showWater,
  showCrops,
  showSoil,
  data,
  refreshData,
}) {
  const readOnly = data?.status === "Accepted";

  const { data: tractWaterNitrate } = useFetch(
    `/tracts/${data?.tract_id}/WaterNitrogen?ReportYear=${data?.report_year}`,
    { prevent: !data?.tract_id || !data?.report_year },
  );

  const [isTractConfirmed, setTractConfirmed] = useState(!requireTractConfirm);

  const [activeTab, setActiveTab] = useState(readOnly ? "confirm" : showWater ? "water" : showCrops ? "crops" : "soil");

  // If refreshing the data gets an accepted status, switch to the confirm tab.
  useEffect(() => {
    if (readOnly) setActiveTab("confirm");
  }, [readOnly]);

  const { statuses, problems } = useReportValidation(data, showWater, showCrops, showSoil);

  const navigate = useNavigate();

  // A string representing the tab to go to on confirm. `undefined` if the dialog should be closed.
  // TODO: We should also block page navigation with this when appropriate.
  const [unsavedDialogNextTab, setUnsavedDialogNextTab] = useState(undefined);

  const advanceLabel = useMemo(() => {
    // TODO: These should be responsive to which parts of the form are complete.
    //       i.e. If water is incomplete, Soil should display "Return to Water".
    switch (activeTab) {
      case "water":
        if (showCrops) return "Continue to Crops";
        if (showSoil) return "Continue to Soil";
        return "Continue to Confirm & Submit";
      case "crops":
        if (showSoil) return "Continue to Soil";
        return "Continue to Confirm & Submit";
      case "soil":
        return "Continue to Confirm & Submit";
      case "confirm":
      default:
        if (showWater && statuses.water.level !== "complete") return "Return to Water";
        if (showCrops && statuses.crops.level !== "complete") return "Return to Crops";
        if (showWater && statuses.soil.level !== "complete") return "Return to Soil";
        break;
    }
  }, [activeTab, showWater, showCrops, showSoil]);

  const meterReportRef = useRef(undefined);
  const cropReportRef = useRef(undefined);
  const soilReportRef = useRef(undefined);

  const advanceTab = useCallback((hasUnsavedChanges) => {
    let nextTab;

    // TODO: See note in advanceLabel about section statuses.
    switch (activeTab) {
      case "water":
        if (showCrops) {
          nextTab = "crops";
        } else if (showSoil) {
          nextTab = "soil";
        } else {
          nextTab = "confirm";
        }
        break;
      case "crops":
        if (showSoil) {
          nextTab = "soil";
        } else {
          nextTab = "confirm";
        }
        break;
      case "soil":
        nextTab = "confirm";
        break;
      case "confirm":
      default:
        if (showWater && statuses.water.level !== "complete") {
          nextTab = "water";
        }
        if (showCrops && statuses.crops.level !== "complete") {
          nextTab = "crops";
        }
        if (showWater && statuses.soil.level !== "complete") {
          nextTab = "soil";
        }
        break;
    }

    if (hasUnsavedChanges) {
      setUnsavedDialogNextTab(nextTab);
    } else {
      setActiveTab(nextTab);
    }
  }, [activeTab, showCrops, showSoil, showWater]);

  const trySetActiveTab = useCallback((tab) => {
    if (tab === activeTab) return;

    if (activeTab === "water" && meterReportRef.current.isDirty) {
      setUnsavedDialogNextTab(tab);
      return;
    } else if (activeTab === "crops" && cropReportRef.current.isDirty) {
      setUnsavedDialogNextTab(tab);
      return;
    } else if (activeTab === "soil" && soilReportRef.current.isDirty) {
      setUnsavedDialogNextTab(tab);
      return;
    }

    setActiveTab(tab);
  }, [activeTab]);

  if (!data) return <></>;

  return (
    <div>
      {!isTractConfirmed ? <div className="mt-10 flex flex-col items-center">
          <div>
            Is the above tract information correct?
          </div>
          <div className="flex gap-8">
            <Button
              className="w-fit mt-5 text-base"
              priority="reject"
              onClick={() => navigate("/reports")}
            >
              &lt; No, back to reports
            </Button>
            <Button
              className="w-fit mt-5 text-base font-bold"
              onClick={() => setTractConfirmed(true)}
            >
              Yes, continue
            </Button>
          </div>
        </div> : <>
        <div className="flex border-t border-r border-l border-slate-400 rounded-t-md">
          {showWater && (
            <ReportTab
              active={activeTab === "water"}
              onClick={() => trySetActiveTab("water")}
              label="Water"
              status={statuses.water}
              disabled={readOnly}
            />
          )}
          {showCrops && (
            <ReportTab
              active={activeTab === "crops"}
              onClick={() => trySetActiveTab("crops")}
              label="Crops"
              status={statuses.crops}
              disabled={readOnly}
            />
          )}
          {showSoil && (
            <ReportTab
              active={activeTab === "soil"}
              onClick={() => trySetActiveTab("soil")}
              label="Soil"
              status={statuses.soil}
              disabled={readOnly}
            />
          )}
          <ReportTab
            active={activeTab === "confirm"}
            onClick={() => trySetActiveTab("confirm")}
            label="Confirm & Submit"
            status={statuses.confirm}
          />
        </div>
        <div className="w-full p-4 border border-slate-400 border-t-0 rounded-b-md">
          {/* FIXME: There's an issue with the data reloading every time you return to a tab. Two
                     easy solutions: (a) Load the data here and pass it into each tab. (b) Always
                     mount the components for the tab, but pass them a parameter for `isVisible`.
          */}
          {activeTab === "water" && <MeterReport
            ref={meterReportRef}
            tractId={data?.tract_id}
            data={data.flowmeter_section}
            reportId={reportId}
            refreshData={refreshData}
            advanceLabel={advanceLabel}
            advanceTab={advanceTab}
            initialProblems={problems.water}
          />}
          {activeTab === "crops" && <>
            <CropReport
              ref={cropReportRef}
              tractAcres={data?.tract_details?.acres || 0}
              tractId={data?.tract_id}
              tractWaterNitrate={tractWaterNitrate?.data.WaterNitrogen}
              reportId={reportId}
              isDryLand={data?.tract_details?.Category === "Dryland"}
              data={data?.crop_section}
              refreshData={refreshData}
              reportYear={data?.report_year}
              advanceLabel={advanceLabel}
              advanceTab={advanceTab}
              initialProblems={problems.crops}
            />
          </>}
          {activeTab === "soil" && <>
            <SoilReport
              ref={soilReportRef}
              tractAcres={data?.tract_details?.acres || 0}
              tract={data?.tract_details}
              reportId={reportId}
              data={data?.soil_section}
              refreshData={refreshData}
              reportYear={data?.report_year}
              advanceLabel={advanceLabel}
              advanceTab={advanceTab}
              initialProblems={problems.soil}
            />
          </>}
          {activeTab === "confirm" && <>
            <ReportConfirmation
              data={data}
              advanceLabel={advanceLabel}
              advanceTab={advanceTab}
              readOnly={data?.status === "Accepted"}
            />
          </>}
        </div>
      </>}
      <ConfirmModal
        isOpen={!!unsavedDialogNextTab}
        title="Unsaved Changes"
        message="You have unsaved changes to the current page. Continue anyway?"
        confirmButtonLabel="Discard and continue"
        toggle={() => setUnsavedDialogNextTab(undefined)}
        onConfirm={() => {
          setUnsavedDialogNextTab(undefined);
          setActiveTab(unsavedDialogNextTab);
        }}
        secondaryButtonText="Cancel"
        warning
      />
    </div>
  );
}
